/**
 * ReviewMetrics
 *
 * Displays review metrics cards for the Content Review Dashboard.
 * Shows: Total Pending, Reviewed (7 days), New (7 days), Approval Rate, Rejection Rate
 *
 * Part of Story 29: Content Review Dashboard with Reviewer Attribution
 *
 * Date: December 16, 2025
 */

import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Clock,
  CheckCircle2,
  XCircle,
  FileText,
  TrendingUp,
  Users
} from 'lucide-react';
import { ReviewMetrics as MetricsData } from '@/services/contentReviewService';

interface ReviewMetricsProps {
  metrics: MetricsData | null;
  loading: boolean;
}

interface MetricCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  description?: string;
  loading?: boolean;
  variant?: 'default' | 'success' | 'warning' | 'danger';
}

function MetricCard({
  title,
  value,
  icon,
  description,
  loading,
  variant = 'default'
}: MetricCardProps) {
  const variantStyles = {
    default: 'text-muted-foreground',
    success: 'text-green-500',
    warning: 'text-yellow-500',
    danger: 'text-red-500'
  };

  if (loading) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">{title}</CardTitle>
          <div className={variantStyles[variant]}>{icon}</div>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-8 w-20 mb-1" />
          {description && <Skeleton className="h-4 w-24" />}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <div className={variantStyles[variant]}>{icon}</div>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">
          {typeof value === 'number' ? value.toLocaleString() : value}
        </div>
        {description && (
          <p className="text-xs text-muted-foreground mt-1">{description}</p>
        )}
      </CardContent>
    </Card>
  );
}

export function ReviewMetrics({ metrics, loading }: ReviewMetricsProps) {
  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
      {metrics && (metrics.discoveryIndexingFailed === undefined || metrics.discoveryIndexingFailed > 0) && (
        <div role="alert" className="col-span-full rounded-md border border-red-300 p-3 text-sm text-red-700">
          {metrics.discoveryIndexingFailed === undefined
            ? 'Discovery indexing status is unavailable.'
            : `${metrics.discoveryIndexingFailed.toLocaleString()} product page(s) failed Discovery indexing after retries. Their search content may be stale; operator recovery is required.`}
        </div>
      )}
      <MetricCard
        title="Total Pending"
        value={metrics?.totalPending || 0}
        icon={<Clock className="h-4 w-4" />}
        description="Awaiting review"
        loading={loading}
        variant={metrics?.totalPending && metrics.totalPending > 10 ? 'warning' : 'default'}
      />

      <MetricCard
        title="Reviewed (7 days)"
        value={metrics?.reviewedInPeriod || 0}
        icon={<CheckCircle2 className="h-4 w-4" />}
        description="Completed reviews"
        loading={loading}
        variant="success"
      />

      <MetricCard
        title="New (7 days)"
        value={metrics?.newInPeriod || 0}
        icon={<FileText className="h-4 w-4" />}
        description="Content created"
        loading={loading}
      />

      <MetricCard
        title="Approval Rate"
        value={`${metrics?.approvalRate || 0}%`}
        icon={<TrendingUp className="h-4 w-4" />}
        description={`${metrics?.approvedCount ?? 0} approved`}
        loading={loading}
        variant={
          metrics?.approvalRate !== undefined
            ? metrics.approvalRate >= 80
              ? 'success'
              : metrics.approvalRate >= 50
              ? 'warning'
              : 'danger'
            : 'default'
        }
      />

      <MetricCard
        title="Rejection Rate"
        value={`${metrics?.rejectionRate || 0}%`}
        icon={<XCircle className="h-4 w-4" />}
        description={`${metrics?.rejectedCount ?? 0} rejected`}
        loading={loading}
        variant={
          metrics?.rejectionRate !== undefined && metrics.rejectionRate > 20
            ? 'danger'
            : 'default'
        }
      />
    </div>
  );
}

interface TopReviewersProps {
  reviewers: Array<{ userId: string; name: string; count: number }>;
  loading: boolean;
}

export function TopReviewers({ reviewers, loading }: TopReviewersProps) {
  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Users className="h-4 w-4" />
            Top Reviewers
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center justify-between">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-8" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!reviewers || reviewers.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Users className="h-4 w-4" />
            Top Reviewers
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">No reviews yet</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Users className="h-4 w-4" />
          Top Reviewers
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {reviewers.map((reviewer, index) => (
            <div key={reviewer.userId} className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground w-4">
                  {index + 1}.
                </span>
                <span className="text-sm font-medium">{reviewer.name}</span>
              </div>
              <span className="text-sm text-muted-foreground">
                {reviewer.count} reviews
              </span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default ReviewMetrics;
